import { ExpandablePanel } from "@/components/Base/ExpandablePanel";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/Base/Tabs";
import { useStore } from "@/stores/useStore";
import { ActivityFeed } from "@/components/ActivityFeed/ActivityFeed";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ConversationRepository } from "@/repositories/conversation";
import { Spinner } from "@/components/Common/Spinner";
import { MessageBoard } from "@/components/MessageBoard/MessageBoard";
import { UsersProvider } from "@/providers/Users.provider";
import { GoList } from "@/components/GoList/GoList";
import { useInboxPanelAnalytics } from "@/hooks/useInboxPanelAnalytics";
import { useTasksAnalytics } from "@/hooks/useTasksAnalytics";
import { InboxPanelTab } from "@/types/v2/Analytics";
import { CustomerUpdatesTab } from "@/components/CustomerUpdate/CustomerUpdatesTab";
import { CustomerUpdatesBadgeCount } from "@/components/CustomerUpdate/CustomerUpdatesBadgeCount";
import { useCustomerUpdates } from "@/hooks/useCustomerUpdates";
import { useShopConfig } from "@/hooks/shop/useShopConfig";
import { DispatchView } from "@/components/DispatchView/DispatchView";
import { useFeature } from "@/hooks/useFeatures";
import { FEATURE_FLAGS } from "@/types/v2/FeatureFlag";
import {
  useRoPanelParams,
  RO_DETAILS_TAB,
  SEARCH_RESULTS_TAB,
} from "@/hooks/useRoPanelParams";
import { useInboxPanel } from "@/hooks/useInboxPanel";
import { RoDetailsTab } from "@/components/Common/RoDetailsPanel/RoDetailsTab";
import { SearchResultsTab } from "@/components/Common/SearchResultsPanel/SearchResultsTab";
import { TasksTab } from "@/components/Tasks/TasksTab";
import { MyOpenTasksBadgeCount } from "@/components/Tasks/MyOpenTasksBadgeCount";
import { useRoPanelParamsSync } from "@/hooks/useRoPanelParamsSync";
import { resolvePanelTab } from "@/lib/inboxPanelTabs";

export const InboxPanel = () => {
  const {
    conversations,
    conversationsLoaded,
    shopId,
    user,
    setCustomerUpdatesTabVisible,
    setTasksTabVisible,
  } = useStore();
  const [conversationId, setConversationId] = useState<string>("");
  const { activeTab, setActiveTab } = useRoPanelParams();
  // Keeps the provider in step with deep links and back/forward navigation.
  useRoPanelParamsSync();
  const { hasSearchResults } = useInboxPanel();
  const { isCustomerUpdatesEnabled } = useCustomerUpdates();
  const shopConfig = useShopConfig();
  const isDispatchViewEnabled = useFeature(FEATURE_FLAGS.isDispatchViewEnabled);
  const isTasksTabEnabled = useFeature(FEATURE_FLAGS.isTasksTabEnabled);

  // Get inbox panel settings with defaults
  const inboxPanelConfig = useMemo(() => {
    return {
      showActivityFeed: shopConfig?.inboxPanel?.showActivityFeed ?? true,
      showMessages: shopConfig?.inboxPanel?.showMessages ?? true,
      showGoList: shopConfig?.inboxPanel?.showGoList ?? true,
      showCustomerUpdates: shopConfig?.inboxPanel?.showCustomerUpdates ?? true,
      showDispatchView: shopConfig?.inboxPanel?.showDispatchView ?? false,
    };
  }, [shopConfig]);

  // Mirrors the TabsTrigger list below, in the same order.
  const visibleTabs = useMemo(() => {
    const tabs: string[] = [];
    if (inboxPanelConfig.showActivityFeed) tabs.push("activityFeed");
    if (inboxPanelConfig.showMessages) tabs.push("messageBoard");
    if (inboxPanelConfig.showGoList) tabs.push("goList");
    if (isDispatchViewEnabled && inboxPanelConfig.showDispatchView) {
      tabs.push("dispatch");
    }
    tabs.push(RO_DETAILS_TAB);
    if (hasSearchResults) tabs.push(SEARCH_RESULTS_TAB);
    if (isTasksTabEnabled) tabs.push("tasks");
    if (isCustomerUpdatesEnabled && inboxPanelConfig.showCustomerUpdates) {
      tabs.push("customerUpdates");
    }
    return tabs;
  }, [
    inboxPanelConfig,
    isDispatchViewEnabled,
    hasSearchResults,
    isTasksTabEnabled,
    isCustomerUpdatesEnabled,
  ]);

  const selectedTab = resolvePanelTab(activeTab, visibleTabs);

  // Initialize analytics hook
  const { trackTabNavigation, startSession } = useInboxPanelAnalytics();
  const { logTasksTabOpened } = useTasksAnalytics();
  const previousTabRef = useRef<InboxPanelTab>(selectedTab as InboxPanelTab);
  useEffect(() => {
    previousTabRef.current = selectedTab as InboxPanelTab;
  }, [selectedTab]);

  // Enhanced tab change handler with analytics tracking
  const handleTabChange = useCallback(
    (value: string) => {
      const fromTab = previousTabRef.current;
      const toTab = value as InboxPanelTab;

      // Track tab navigation analytics
      try {
        if (fromTab !== toTab) {
          trackTabNavigation(fromTab, toTab, undefined, "click");
        }
      } catch (error) {
        console.error("Failed to track tab navigation:", error);
      }

      if (toTab === "tasks" && fromTab !== toTab) {
        logTasksTabOpened("tab_click");
      }

      // Update customer updates tab visibility for toast management
      setCustomerUpdatesTabVisible(toTab === "customerUpdates");
      setTasksTabVisible(toTab === "tasks");

      setActiveTab(value, { persist: value !== SEARCH_RESULTS_TAB });
    },
    [
      trackTabNavigation,
      logTasksTabOpened,
      setCustomerUpdatesTabVisible,
      setTasksTabVisible,
      setActiveTab,
    ]
  );

  const conversation = useMemo(
    () => conversations.find((c) => c.id === conversationId),
    [conversations, conversationId]
  );

  // Initialize session tracking when component mounts
  useEffect(() => {
    startSession(selectedTab as InboxPanelTab);
  }, [startSession, selectedTab]);

  // Initialize customer updates tab visibility state
  useEffect(() => {
    setCustomerUpdatesTabVisible(selectedTab === "customerUpdates");
  }, [selectedTab, setCustomerUpdatesTabVisible]);

  // Initialize tasks tab visibility state
  useEffect(() => {
    setTasksTabVisible(selectedTab === "tasks");
  }, [selectedTab, setTasksTabVisible]);

  useEffect(() => {
    if (shopId && conversationsLoaded && conversations.length === 0) {
      ConversationRepository.create(shopId, {
        type: "1:everyone",
        name: null,
        participants: [],
      });
    } else {
      setConversationId(
        conversations.find((c) => c.type === "1:everyone")?.id ?? ""
      );
    }
  }, [conversationsLoaded, conversations, shopId]);

  if (!conversation || !user || !shopId) {
    return (
      <ExpandablePanel headerless={true}>
        <div className="flex h-full w-full items-center justify-center">
          <Spinner />
        </div>
      </ExpandablePanel>
    );
  }

  return (
    <UsersProvider>
      <ExpandablePanel
        side="right"
        headerless={true}
        className="flex flex-col gap-0 overflow-hidden border-0 p-0"
      >
        <Tabs
          value={selectedTab}
          onValueChange={handleTabChange}
          className="flex h-full w-full flex-col overflow-hidden rounded-none border-0 p-0"
        >
          <TabsList
            className="flex h-fit w-full shrink-0 flex-row items-center justify-start gap-0.5 overflow-x-auto overflow-y-hidden rounded-none bg-slate-900 p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            variant="default"
          >
            {inboxPanelConfig.showActivityFeed && (
              <TabsTrigger
                className="h-10 shrink-0 rounded-md px-2.5 py-2 font-sans text-[13px] tracking-[-0.01em] text-white opacity-90 data-[state=active]:bg-slate-100 data-[state=active]:font-bold data-[state=active]:text-slate-900 data-[state=active]:opacity-100 data-[state=active]:shadow-none data-[state=inactive]:hover:bg-slate-800"
                value="activityFeed"
                variant="default"
              >
                Activity
              </TabsTrigger>
            )}
            {inboxPanelConfig.showMessages && (
              <TabsTrigger
                variant="default"
                className="h-10 shrink-0 rounded-md px-2.5 py-2 font-sans text-[13px] tracking-[-0.01em] text-white opacity-90 data-[state=active]:bg-slate-100 data-[state=active]:font-bold data-[state=active]:text-slate-900 data-[state=active]:opacity-100 data-[state=active]:shadow-none data-[state=inactive]:hover:bg-slate-800"
                value="messageBoard"
              >
                Messages
              </TabsTrigger>
            )}
            {inboxPanelConfig.showGoList && (
              <TabsTrigger
                variant="default"
                className="h-10 shrink-0 rounded-md px-2.5 py-2 font-sans text-[13px] tracking-[-0.01em] text-white opacity-90 data-[state=active]:bg-slate-100 data-[state=active]:font-bold data-[state=active]:text-slate-900 data-[state=active]:opacity-100 data-[state=active]:shadow-none data-[state=inactive]:hover:bg-slate-800"
                value="goList"
              >
                Go List
              </TabsTrigger>
            )}
            {isDispatchViewEnabled && inboxPanelConfig.showDispatchView && (
              <TabsTrigger
                variant="default"
                className="h-10 shrink-0 rounded-md px-2.5 py-2 font-sans text-[13px] tracking-[-0.01em] text-white opacity-90 data-[state=active]:bg-slate-100 data-[state=active]:font-bold data-[state=active]:text-slate-900 data-[state=active]:opacity-100 data-[state=active]:shadow-none data-[state=inactive]:hover:bg-slate-800"
                value="dispatch"
              >
                Dispatch
              </TabsTrigger>
            )}
            <TabsTrigger
              variant="default"
              className="h-10 shrink-0 rounded-md px-2.5 py-2 font-sans text-[13px] tracking-[-0.01em] text-white opacity-90 data-[state=active]:bg-slate-100 data-[state=active]:font-bold data-[state=active]:text-slate-900 data-[state=active]:opacity-100 data-[state=active]:shadow-none data-[state=inactive]:hover:bg-slate-800"
              value="roDetails"
            >
              RO Details
            </TabsTrigger>
            {hasSearchResults && (
              <TabsTrigger
                variant="default"
                className="h-10 shrink-0 rounded-md px-2.5 py-2 font-sans text-[13px] tracking-[-0.01em] text-white opacity-90 data-[state=active]:bg-slate-100 data-[state=active]:font-bold data-[state=active]:text-slate-900 data-[state=active]:opacity-100 data-[state=active]:shadow-none data-[state=inactive]:hover:bg-slate-800"
                value="searchResults"
              >
                Search Results
              </TabsTrigger>
            )}
            {isTasksTabEnabled && (
              <TabsTrigger
                variant="default"
                className="h-10 shrink-0 rounded-md px-2.5 py-2 font-sans text-[13px] tracking-[-0.01em] text-white opacity-90 data-[state=active]:bg-slate-100 data-[state=active]:font-bold data-[state=active]:text-slate-900 data-[state=active]:opacity-100 data-[state=active]:shadow-none data-[state=inactive]:hover:bg-slate-800"
                value="tasks"
              >
                <MyOpenTasksBadgeCount
                  className={"relative right-0 top-0 mr-2"}
                />
                Tasks
              </TabsTrigger>
            )}
            {isCustomerUpdatesEnabled &&
              inboxPanelConfig.showCustomerUpdates && (
                <TabsTrigger
                  variant="default"
                  className="h-10 shrink-0 rounded-md px-2.5 py-2 font-sans text-[13px] tracking-[-0.01em] text-white opacity-90 data-[state=active]:bg-slate-100 data-[state=active]:font-bold data-[state=active]:text-slate-900 data-[state=active]:opacity-100 data-[state=active]:shadow-none data-[state=inactive]:hover:bg-slate-800"
                  value="customerUpdates"
                >
                  <CustomerUpdatesBadgeCount
                    className={"relative right-0 top-0 mr-2"}
                  />
                  Customer Updates
                </TabsTrigger>
              )}
          </TabsList>
          <div className="w-full flex-1 overflow-hidden">
            {inboxPanelConfig.showActivityFeed && (
              <TabsContent
                value="activityFeed"
                className="h-full w-full overflow-auto"
              >
                <ActivityFeed />
              </TabsContent>
            )}
            {inboxPanelConfig.showMessages && (
              <TabsContent
                value="messageBoard"
                className="h-full w-full overflow-auto"
              >
                <MessageBoard conversation={conversation} shopId={shopId} />
              </TabsContent>
            )}
            {inboxPanelConfig.showGoList && (
              <TabsContent
                value="goList"
                className="h-full w-full overflow-auto"
              >
                <GoList showOpenButton={true} />
              </TabsContent>
            )}
            {isDispatchViewEnabled && inboxPanelConfig.showDispatchView && (
              <TabsContent
                value="dispatch"
                className="h-full w-full overflow-auto"
              >
                <DispatchView />
              </TabsContent>
            )}
            <TabsContent
              value="roDetails"
              className="h-full w-full overflow-auto"
            >
              <RoDetailsTab />
            </TabsContent>
            {hasSearchResults && (
              <TabsContent
                value="searchResults"
                className="h-full w-full overflow-auto"
              >
                <SearchResultsTab />
              </TabsContent>
            )}
            {isTasksTabEnabled && (
              <TabsContent
                value="tasks"
                className="h-full w-full overflow-auto"
              >
                <TasksTab />
              </TabsContent>
            )}
            {isCustomerUpdatesEnabled &&
              inboxPanelConfig.showCustomerUpdates && (
                <TabsContent
                  value="customerUpdates"
                  className="h-full w-full overflow-auto"
                >
                  <CustomerUpdatesTab />
                </TabsContent>
              )}
          </div>
        </Tabs>
      </ExpandablePanel>
    </UsersProvider>
  );
};
